import React, { useEffect, useState } from "react";
import { createCheckoutSubscriptionSession, useApiInfo } from "../endpoints/index.js";
import BFLoading from '../components/small/BFLoading';
import BFIcon from '../components/BFIcon'

export default function APILandingPage({ setShowModalType }) {
  const [isYearly, setIsYearly] = useState(false); // Toggle between Monthly and Yearly
  const [openFAQ, setOpenFAQ] = useState(null); // To track which FAQ is open
  const [isLogin, setLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userApiData, setUserApiData] = useState([]);
  const [hasKey, setHasKey] = useState(true);
  const [tier, setTier] = useState("free");

  // In your component
  const { data, isValid, isLoading: apiLoading } = useApiInfo();

  useEffect(() => {
    setIsLoading(true);

    if (apiLoading) return; // Don't process data while loading

    if (data && data.length > 0) {
      setHasKey(true);
      const processedData = data.map(d => ({
        ...d,
        status: d.active ? 'Active' : 'Inactive'
      }));

      const activeItem = processedData.find(d => d.active);
      if (activeItem) {
        setTier(activeItem.tier);
      }

      setUserApiData(processedData);
    } else if (isValid) {
      setHasKey(false);
      setTier('free');
    } else {
      setHasKey(false);
      setTier('free');
      setUserApiData([]);
    }

    setIsLoading(false);
  }, [data, isValid, apiLoading]);

  const isFreeDisabled = (userTier, plan) => {
    if (userTier !== 'free' && plan === 'Free') {
      return true;
    }
    return false;
  };

  const getButton = (userTier, plan, text) => {
    if (userTier.length > 0) {
      return text;
    }
    return text;
  };

  const togglePricing = () => {
    setIsYearly((prev) => !prev);
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  // Add a `billedMonthlyText` for the plans that need it:
  const plans = [
    {
      name: "Free",
      monthlyPrice: 0,
      yearlyPrice: 0,
      // This one doesnt really need a billedMonthlyText or billedYearlyText

      oldYearlyPrice: "Free",
      newYearlyPrice: "(Still Free)",

      billedMonthlyText: "Free",
      features: [
        "5k call credits/mo",
        "10 calls/min",
        "5 data endpoints",
        "3 months historical data*",
        "Personal use",
      ],
      buttonText: "Get Your Free API Key",
    },
    {
      name: "Starter",
      monthlyPrice: 15,
      yearlyPrice: 13.50,

      oldYearlyPrice: "$180",
      newYearlyPrice: "$162/year",

      billedMonthlyText: "Or $13.50/mo billed yearly",

      features: [
        "250k call credits/mo",
        "20 calls/min",
        "10 data endpoints",
        "6 months historical data",
        "Personal use",
      ],
      buttonText: "Get Started",
    },
    {
      name: "Analyst",
      monthlyPrice: 49,
      yearlyPrice: 42,

      oldYearlyPrice: "$588",
      newYearlyPrice: "$504/year",

      billedMonthlyText: "Or $42/mo billed yearly",

      features: [
        "250k call credits/mo",
        "20 calls/min",
        "10 data endpoints",
        "3 years of historical data",
        "Personal use",
      ],
      buttonText: "Get Started",
    },
    {
      name: "Professional",
      monthlyPrice: 79,
      yearlyPrice: 64,

      oldYearlyPrice: "$948",
      newYearlyPrice: "$768/year",

      billedMonthlyText: "Or $64/mo billed yearly",

      features: [
        "250k call credits/mo",
        "30 calls/min",
        "10 data endpoints",
        "All time historical data",
        "Commercial use",
      ],
      buttonText: "Get Started",
    },
    {
      name: "Enterprise",
      monthlyPrice: "",
      yearlyPrice: "",
      features: [
        "2M+ call credits/mo",
        "Custom calls/min limit",
        "All data endpoints, Beta Features",
        "All time historical data",
        "Commercial use",
      ],
      buttonText: "Contact Us",
    },
  ];

  const comparisonData = {
    headers: ["Feature", "Free", "Starter", "Analyst", "Professional", "Enterprise"],
    rows: [
      ["Monthly API Credits", "5k", "250k", "250k", "250k", "2 Million"],
      ["Rate Limit (calls/min)", "10", "20", "20", "30", "Custom"],
      ["Commercial Use", "", "", "", <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />],
      ["Historical Data", "3 months *(daily only)", "6 months", "3 years", "All time", "All time"],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-browsable-indexes"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Browsable Indexes
        </a>,
        <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-all-coins"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          All Coins
        </a>,
        <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-coin-data"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Coin Data
        </a>,
        <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-sector-indexes"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Sector Indexes
        </a>,
        <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-bitformance-altcoin-index"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Bitformance Altcoin Index
        </a>,
        "", <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-altcoin-season-indicator"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Altcoin Season Index
        </a>,
        "", <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-taxonomies"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Taxonomies
        </a>,
        <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-user-indexes"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          User Indexes
        </a>,
        "", <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-index-data"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Index Data
        </a>,
        "", <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-top-200-data"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Top 200 Data
        </a>,
        "", <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      [
        <a
          href="https://bitformance.com/api/documentation/#get-rebalancing-history"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Rebalancing History (Public Indexes)
        </a>,
        "", <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />, <BFIcon iconName="checked-circle" color="green" />
      ],
      ["Beta Features", "", "", "", "", <BFIcon iconName="checked-circle" color="green" />]
    ],
  };

  const handleSelect = async (price_id, interval) => {
    const username = localStorage.getItem('username');

    if (!!username === false) {
      setShowModalType('SIGN_IN');
      return;
    }
    if (hasKey && tier !== 'free' && price_id !== 'free') {
      setShowModalType('CONTACT_US');
      return;
    }
    if (price_id === 'free') {
      window.location.href = "/api/dashboard";
      return;
    }
    if (price_id === 'enterprise') {
      setShowModalType('CONTACT_US');
      return;
    }

    try {
      setIsLoading(true);
      console.log(price_id, interval);
      const checkoutResponse = await createCheckoutSubscriptionSession(price_id, interval);
      console.log(checkoutResponse);
      window.location.href = checkoutResponse['redirect'];
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <BFLoading />;

  return (
    <div className="bg-gray-100 text-gray-800">
      {/* Hero Section */}
      <div className="text-center py-20 bg-white">
        <h1 className="text-4xl font-bold text-black">Bitformance API</h1>
        <p className="mt-4 text-lg text-gray-600">
          Cryptocurrency historical data, market metrics, index data, and more all in one API.
        </p>
        <div className="mt-8 flex justify-center space-x-4">
          <button
            className="bg-blue-600 text-white px-6 py-3 rounded shadow hover:bg-blue-700"
            onClick={() => handleSelect('free','monthly')}
          >
            Get Started for Free
          </button>
          <a
            href="https://bitformance.com/api/documentation/"
            className="text-blue-600 font-medium hover:underline inline-block self-center"
          >
            Explore Docs &gt;
          </a>
        </div>
        <div className="mt-8">
          <p className="text-sm text-gray-500">Get started for free, no credit card required</p>
        </div>
      </div>

      {/*Will use this later once we get more customers
      <div className="py-10 bg-gray-50">
        <div className="container mx-auto text-center">
          <h2 className="text-xl font-bold text-gray-700">Trusted By</h2>
          <div className="mt-6 flex justify-center space-x-6">
            <img src="/RV-Logo-BLACK.png" alt="RealVision Logo" className="h-11" />
            <img src="/placeholder2.png" alt="Company 2" className="h-10" />
            <img src="/placeholder3.png" alt="Company 3" className="h-10" />
            <img src="/placeholder4.png" alt="Company 4" className="h-10" />
          </div>
        </div>
      </div>*/}

      {/* Pricing Section */}
	  <div className="py-20 bg-gray-50">
	    <div className="container mx-auto px-6 text-center">
	      <h2 className="text-3xl font-bold">Bitformance API Plans</h2>
	      <p className="mt-4 text-lg text-gray-600">
	        Start integrating for free or choose a plan which best suits your needs.
	      </p>

	      {/* Pricing Toggle */}
	      <div className="mt-6 flex justify-center items-center">
	        {/* Monthly text: fixed width so bold text doesnt push content */}
	        <span
	          className={`inline-block w-16 text-center mr-2 ${
	            !isYearly ? "text-black font-bold" : "text-gray-600"
	          }`}
	        >
	          Monthly
	        </span>

	        <label className="relative inline-block w-12 h-6">
	          <input
	            type="checkbox"
	            className="sr-only"
	            onChange={togglePricing}
	            checked={isYearly}
	          />
	          {/* The gray track */}
	          <span className="block w-full h-full bg-gray-300 rounded-full shadow-inner"></span>
	          {/* The blue knob */}
	          <span
	            className={`dot absolute top-0 left-0 w-6 h-6 bg-blue-600 rounded-full shadow transform transition ${
	              isYearly ? "translate-x-6" : ""
	            }`}
	          ></span>
	        </label>

	        {/* Yearly text: fixed width so bold text doesnt push content */}
	        <span
	          className={`inline-block w-16 text-center ml-2 ${
	            isYearly ? "text-black font-bold" : "text-gray-600"
	          }`}
	        >
	          Yearly
	        </span>

	        <span className="ml-2 text-sm text-green-600 font-semibold">
	          Save up to 20%
	        </span>
	      </div>

	      {/* Pricing Cards */}
		  <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-8 items-stretch">
		    {plans.map((plan, index) => {
		      const isEnterprise = plan.name === 'Enterprise';
		      const currentPrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;

		      return (
		        <div
		          key={index}
		          className="border border-gray-200 p-6 rounded shadow bg-white flex flex-col h-full"
		        >
		          {/* TOP SECTION that grows to fill space */}
		          <div className="flex-grow flex flex-col justify-between">
		            {/* Title + Price Area */}
		            <div>
		              <h3 className="md:text-xl font-bold">{plan.name}</h3>

		              <div className="mt-2">
		                {/* Main price or "Contact Us" */}
		                {!isEnterprise ? (
		                  <p className="md:text-4xl font-bold">
		                    ${currentPrice}
		                    <span className="text-sm font-light">/mo</span>
		                  </p>
		                ) : (
		                  <p className="md:text-3xl font-bold">Custom</p>
		                )}

		                {/* Show strikethrough + new price if yearly */}
		                {isYearly && plan.oldYearlyPrice && plan.newYearlyPrice && (
		                  <p className="mt-2 text-sm text-gray-500">
		                    <span className="line-through mr-2">{plan.oldYearlyPrice}</span>
		                    <span className="text-green-600">{plan.newYearlyPrice}</span>
		                  </p>
		                )}

		                {/* Show monthly note if not yearly */}
		                {!isYearly && plan.billedMonthlyText && (
		                  <p className="mt-2 text-sm text-gray-500">
		                    {plan.billedMonthlyText}
		                  </p>
		                )}
		              </div>
		            </div>

		            {/* Button at the bottom of the top section */}
		            <button
		              className="mt-4 bg-blue-600 text-white px-1 py-2 rounded shadow hover:bg-blue-700"
		              onClick={() =>
		                isEnterprise
		                  ? handleSelect("enterprise", "monthly")
		                  : isYearly
		                    ? handleSelect(plan.name.toLowerCase(), "yearly")
		                    : handleSelect(plan.name.toLowerCase(), "monthly")
		              }
		            >
		              {plan.buttonText}
		            </button>
		          </div>

		          {/* FEATURES SECTION (below the top) */}
		          <ul className="mt-4 text-sm text-gray-600 space-y-2 text-left">
		            {plan.features.map((feature, i) => (
		              <li key={i}>{feature}</li>
		            ))}
		          </ul>
		        </div>
		      );
		    })}
		  </div>

</div>
</div>

      {/* Comparison Table Section */}
<div className="py-10 bg-white">
  <div className="container mx-auto px-6">
    <h2 className="text-3xl font-bold text-center">
      Compare Plans and Features
    </h2>

    <div className="mt-8 overflow-x-auto">
      <table className="table-fixed w-full border-collapse">
        {/* 6 columns total: Feature + 5 Plans */}
        <colgroup>
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-2/12" />
          <col className="w-2/12" />
        </colgroup>

        {/* ======== TABLE HEAD ======== */}
        <thead className="border-b border-gray-300">

          {/* 1) Plan Names Row */}
          <tr className="text-gray-800">
            {comparisonData.headers.map((header, index) => (
              <th
                key={index}
                // Reduced vertical padding to bring plan names & prices closer
                className={`px-3 py-1 font-bold ${
                  index === 0 ? "text-left" : "text-center"
                } text-xxs sm:text-xs lg:text-lg`}
              >
                {header}
              </th>
            ))}
          </tr>

          {/* 2) Plan Prices Row */}
          <tr className="text-gray-700 text-xxs sm:text-xs">
            {/* Blank cell for Feature column */}
            <th className="px-3 py-0 text-left font-normal" />

            {plans.map((plan, index) => {
              let priceText = "";
              if (plan.name === "Enterprise") {
                priceText = "Contact";
              } else {
                const numPrice = isYearly ? plan.yearlyPrice : plan.monthlyPrice;
                priceText = `$${numPrice}/mo`;
              }

              return (
                <th
                  key={index}
                  // Very little vertical padding + extra margin below the price
                  className="px-3 py-0 text-center font-medium align-top"
                >
                  <span className="block mb-3">{priceText}</span>
                </th>
              );
            })}
          </tr>
        </thead>

        {/* ======== TABLE BODY ======== */}
        <tbody className="text-xxs sm:text-sm text-gray-700">
          {comparisonData.rows.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              className="border-b border-gray-200 last:border-none hover:bg-gray-50"
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`px-3 py-4 ${
                    cellIndex === 0 ? "text-left" : "text-center"
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
</div>

      {/* FAQ Section */}
<div className="py-10 bg-white">
  <div className="container mx-auto px-6">
    <h2 className="text-3xl font-bold text-center">Frequently Asked Questions</h2>
    <div className="mt-8 space-y-4">
      {[
        {
          question: "Is the Bitformance API free?",
          answer: "Bitformance offers both free and paid API plans."
        },
        {
          question: "Are there discounts for yearly plans?",
          answer: "Yes! Depending on the tier, we offer between 10-20% discounts for yearly plans. This discount is automatically applied during checkout."
        },
		{
		  question: "How do I cancel my subscription?",
		  answer: "To cancel your current plan, click on \"Manage Account\" when you visit your ",
		  linkText: "API Dashboard",
		  linkUrl: "/api/dashboard"
		},
        {
          question: "How do I upgrade my current plan?",
          answer: "If you would like to upgrade your current plan, please ",
		  linkText: "contact us.",
	      linkAction: "CONTACT_US"
        },
        {
          question: "What does 'commercial use' mean in the pricing plans?",
          answer: "'Commercial Use' indicates that the chosen API plan allows you to integrate Bitformance data into business products, services, or applications that you plan to sell, distribute, or otherwise leverage in a business setting."
        }
      ].map((faq, index) => (
        <div key={index} className="border border-gray-200 p-4 rounded shadow">

          {/* FAQ Header (Question + Arrow) */}
          <div
            className="flex justify-between items-center cursor-pointer"
            onClick={() => toggleFAQ(index)}
          >
            <h3 className="text-lg font-medium">{faq.question}</h3>

            {/* Arrow Icon (rotates on open) */}
            <svg
              className={`w-4 h-4 text-gray-600 transform transition-transform duration-300 ${
                openFAQ === index ? "rotate-180" : "rotate-0"
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {/* Collapsible Answer */}
          <div
            className={`
              transition-all duration-300 overflow-hidden
              ${openFAQ === index ? "max-h-60 mt-2" : "max-h-0"}
            `}
          >
		  <p className="text-sm text-gray-600">
            {faq.answer}
			{faq.linkText && faq.linkUrl && (
	            <a
	              href={faq.linkUrl}
	              className="text-blue-600 hover:text-blue-800 font-medium"
	            >
	              {faq.linkText}
	            </a>
	          )}
	          {faq.linkText && faq.linkAction && (
	            <a
	              href="#"
	              onClick={(e) => {
	                e.preventDefault();
	                setShowModalType(faq.linkAction);
	              }}
	              className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
	            >
	              {faq.linkText}
	            </a>
	          )}
          </p>
          </div>
        </div>
      ))}
    </div>
  </div>
  {/* Questions Row */}
<div className="mt-20 mb-14 flex items-center justify-center space-x-4">
  <p className="text-xl font-medium text-gray-800">
    Have a question?
  </p>
  <button
    className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
    onClick={() => setShowModalType('CONTACT_US')}
  >
    Contact Us
  </button>
</div>

</div>

    </div>
  );
}
